import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { ArrowDown, AlertTriangle, Sparkles } from 'lucide-react';
import { FieldData, NDVIData } from './types';
import { satelliteApi } from './satelliteApi';
import { cn } from '@/shared/lib/utils';

interface NDVIVisualizationProps {
  fieldData: FieldData;
  dateRange?: { start: string; end: string };
}

export const NDVIVisualization: React.FC<NDVIVisualizationProps> = ({
  fieldData,
  dateRange,
}) => {
  const { t } = useTranslation();
  const [ndviData, setNdviData] = useState<NDVIData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const endDate = dateRange?.end || new Date().toISOString().split('T')[0];
  const startDate =
    dateRange?.start ||
    new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  useEffect(() => {
    fetchData();
  }, [fieldData.id, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await satelliteApi.fetchNDVIData(fieldData, startDate, endDate);
      setNdviData(data);
    } catch (err) {
      setError(t('satellite.loadError', 'Failed to load satellite data. Showing estimated values.'));
    } finally {
      setLoading(false);
    }
  };

  const currentNDVI = ndviData.length > 0 ? ndviData[ndviData.length - 1].value : 0;
  const previousNDVI = ndviData.length > 1 ? ndviData[ndviData.length - 2].value : currentNDVI;
  const trend = currentNDVI - previousNDVI;
  const regionalAverage = 0.65; // Mock value

  const getHealthStatus = (ndvi: number) => {
    if (ndvi > 0.7) return { icon: '🟢', hasSparkles: true, plantColor: 'text-green-600', bgColor: 'bg-green-100', rotation: 0 };
    if (ndvi > 0.5) return { icon: '🟢', hasSparkles: false, plantColor: 'text-green-500', bgColor: 'bg-green-50', rotation: 0 };
    if (ndvi > 0.3) return { icon: '🟡', hasSparkles: false, plantColor: 'text-yellow-600', bgColor: 'bg-yellow-50', rotation: -5 };
    return { icon: '🔴', hasSparkles: false, plantColor: 'text-orange-800', bgColor: 'bg-orange-50', rotation: -15 };
  };

  const health = getHealthStatus(currentNDVI);
  const regionalHealth = getHealthStatus(regionalAverage);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <Skeleton className="h-40 w-40 mx-auto rounded-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="py-8 space-y-8">
          {/* Main Plant Illustration */}
          <div className="flex flex-col items-center gap-6">
            {/* Plant visual */}
            <div className={cn(
              "relative w-40 h-40 rounded-full flex items-center justify-center transition-all duration-500",
              health.bgColor
            )}>
              {/* Soil base */}
              <div className="absolute bottom-8 w-32 h-4 bg-gradient-to-b from-amber-800/60 to-amber-900/80 rounded-full" />
              
              {/* Plant icon */}
              <svg
                viewBox="0 0 100 100"
                className={cn(
                  "w-24 h-24 transition-all duration-500",
                  health.plantColor
                )}
                style={{ transform: `rotate(${health.rotation}deg)` }}
              >
                {/* Stem */}
                <path
                  d="M 50 80 Q 48 60, 50 40"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                />
                {/* Left leaf */}
                <ellipse
                  cx="35"
                  cy="50"
                  rx="12"
                  ry="18"
                  fill="currentColor"
                  opacity="0.9"
                  transform="rotate(-30 35 50)"
                />
                {/* Right leaf */}
                <ellipse
                  cx="65"
                  cy="45"
                  rx="12"
                  ry="18"
                  fill="currentColor"
                  opacity="0.9"
                  transform="rotate(30 65 45)"
                />
                {/* Top leaves */}
                <ellipse
                  cx="50"
                  cy="35"
                  rx="15"
                  ry="20"
                  fill="currentColor"
                />
              </svg>
              
              {/* Sparkles for excellent health */}
              {health.hasSparkles && (
                <>
                  <Sparkles className="absolute top-4 right-4 h-5 w-5 text-yellow-400 animate-pulse" />
                  <Sparkles className="absolute top-8 left-6 h-4 w-4 text-yellow-300 animate-pulse" style={{ animationDelay: '0.3s' }} />
                </>
              )}
            </div>

            {/* Health status icon */}
            <div className="text-5xl">
              {health.icon}
              {health.hasSparkles && '✨'}
            </div>

            {/* Trend indicator */}
            {trend < -0.02 && (
              <div className="flex items-center gap-2 text-red-600">
                <ArrowDown className="h-6 w-6" />
              </div>
            )}
          </div>

          {/* Comparison: Your Field vs Region */}
          <div className="mt-8 p-6 rounded-2xl bg-muted/30">
            <div className="flex items-center justify-center gap-8">
              {/* Your field */}
              <div className="flex flex-col items-center gap-3">
                <div className="text-sm text-muted-foreground">{t('satellite.yourField', 'Your field')}</div>
                <svg
                  viewBox="0 0 100 100"
                  className={cn(
                    "w-16 h-16",
                    health.plantColor
                  )}
                  style={{ transform: `rotate(${health.rotation}deg)` }}
                >
                  <path d="M 50 80 Q 48 60, 50 40" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" />
                  <ellipse cx="35" cy="50" rx="10" ry="15" fill="currentColor" opacity="0.9" transform="rotate(-30 35 50)" />
                  <ellipse cx="65" cy="45" rx="10" ry="15" fill="currentColor" opacity="0.9" transform="rotate(30 65 45)" />
                  <ellipse cx="50" cy="35" rx="12" ry="16" fill="currentColor" />
                </svg>
              </div>

              {/* Comparison arrow */}
              <div className="text-4xl text-muted-foreground">
                {currentNDVI < regionalAverage ? '<' : '>'}
              </div>

              {/* Region average */}
              <div className="flex flex-col items-center gap-3">
                <div className="text-sm text-muted-foreground">{t('satellite.region', 'Region')}</div>
                <svg
                  viewBox="0 0 100 100"
                  className={cn(
                    "w-16 h-16",
                    regionalHealth.plantColor
                  )}
                  style={{ transform: `rotate(${regionalHealth.rotation}deg)` }}
                >
                  <path d="M 50 80 Q 48 60, 50 40" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" />
                  <ellipse cx="35" cy="50" rx="10" ry="15" fill="currentColor" opacity="0.9" transform="rotate(-30 35 50)" />
                  <ellipse cx="65" cy="45" rx="10" ry="15" fill="currentColor" opacity="0.9" transform="rotate(30 65 45)" />
                  <ellipse cx="50" cy="35" rx="12" ry="16" fill="currentColor" />
                </svg>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
